import { useFillPack } from "./FillPackContext";
import { getAnchorCoordinates } from "./PathPopulateContext";
import { useMemo, type ReactNode } from "react";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";
import {
    getGradientColor,
    paletteColorForPathIndex,
} from "../../utils/svgUtils";
import {
    paletteColorIndexForPlacement,
    randomHexColorForPlacement,
} from "../../utils/pathPopulatePlacement";
import type { FillPackProgress } from "../../utils/fillPackPlacement";
import { serializeSvgInnerAsStrokeGuides } from "../../utils/fillPackGeometry";

function applyFillToSvgContent(svgContent: string, fillColor: string): string {
    if (!svgContent) return "";
    const elementRegex =
        /<(path|circle|rect|ellipse|polygon|polyline|line)([^>]*?)(\s*\/)?>/gi;
    return svgContent.replace(
        elementRegex,
        (_match, tagName: string, attributes: string, selfClose: string) => {
            const cleanedAttributes = attributes.replace(
                /\s*fill\s*=\s*["'][^"']*["']/gi,
                "",
            );
            const closing = selfClose ? " />" : ">";
            return `<${tagName}${cleanedAttributes} fill="${fillColor}"${closing}`;
        },
    );
}

const PROGRESS_LABELS: Record<FillPackProgress["phase"], string> = {
    mount: "Loading SVG…",
    scan: "Scanning regions",
    filter: "Testing fill grid",
};

function formatProgress(info: FillPackProgress | null): string {
    if (!info) return "Computing…";
    const label = PROGRESS_LABELS[info.phase];
    if (info.total <= 0) return `${label}…`;
    const pct = Math.min(
        100,
        Math.max(0, Math.round((info.processed / info.total) * 100)),
    );
    return `${label} (${pct}%)`;
}

function renderFooterMeta(input: {
    pathSvg: unknown;
    objectInnerSvgs: unknown[];
    placementComputePending: boolean;
    placementComputeProgress: FillPackProgress | null;
    regionCount: number;
    placements: unknown[];
    hasGeneratedPlacementsForCurrentParams: boolean;
    estimatedPlacementCount: number;
    supportsFillHitTest: boolean;
}): ReactNode {
    const {
        pathSvg,
        objectInnerSvgs,
        placementComputePending,
        placementComputeProgress,
        regionCount,
        placements,
        hasGeneratedPlacementsForCurrentParams,
        estimatedPlacementCount,
        supportsFillHitTest,
    } = input;

    if (!pathSvg) return "Load object and path SVGs.";
    if (objectInnerSvgs.length === 0) {
        return "Load at least one object SVG.";
    }
    if (!supportsFillHitTest) {
        return "This browser does not support fill hit-testing (isPointInFill). Try another browser.";
    }
    if (regionCount === 0) {
        return "No closed, filled shapes found in the path SVG.";
    }
    if (placementComputePending) {
        return formatProgress(placementComputeProgress);
    }
    if (!hasGeneratedPlacementsForCurrentParams) {
        return (
            <>
                Up to <strong>~{estimatedPlacementCount}</strong> grid cell
                {estimatedPlacementCount === 1 ? "" : "s"} at current spacing
                across <strong>{regionCount}</strong> filled region
                {regionCount === 1 ? "" : "s"}. Click <strong>Generate</strong>{" "}
                to pack.
            </>
        );
    }
    return (
        <>
            <strong>{placements.length}</strong> instance
            {placements.length === 1 ? "" : "s"} in{" "}
            <strong>{regionCount}</strong> region{regionCount === 1 ? "" : "s"}.
        </>
    );
}

export function FillPackViewer() {
    const {
        pathSvg,
        placements,
        estimatedPlacementCount,
        hasGeneratedPlacementsForCurrentParams,
        generatePlacements,
        placementComputePending,
        placementComputeProgress,
        cancelPlacementCompute,
        objectInnerSvgs,
        viewBoxAttr,
        anchor,
        scaleFactor,
        gradientEnabled,
        gradientMode,
        gradientColors,
        maxGradientSteps,
        canExport,
        handleDownload,
        showPathGuides,
        pathGuideStroke,
        pathGuideStrokeWidth,
        regionCount,
        supportsFillHitTest,
        packRegionEnabled,
        packRegionMinX,
        packRegionMinY,
        packRegionMaxX,
        packRegionMaxY,
    } = useFillPack();

    const packRegionRect = useMemo(() => {
        if (!packRegionEnabled) return null;
        const minX = Math.min(packRegionMinX, packRegionMaxX);
        const maxX = Math.max(packRegionMinX, packRegionMaxX);
        const minY = Math.min(packRegionMinY, packRegionMaxY);
        const maxY = Math.max(packRegionMinY, packRegionMaxY);
        if (maxX <= minX || maxY <= minY) return null;
        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
        };
    }, [
        packRegionEnabled,
        packRegionMinX,
        packRegionMinY,
        packRegionMaxX,
        packRegionMaxY,
    ]);

    const maxRi = useMemo(() => {
        if (placements.length === 0) return 0;
        return placements.reduce((a, p) => Math.max(a, p.regionIndex), 0);
    }, [placements]);
    const nPl = placements.length;

    const guideMarkup = useMemo(() => {
        if (!pathSvg || !showPathGuides) return "";
        return serializeSvgInnerAsStrokeGuides(
            pathSvg.content,
            pathGuideStroke,
            pathGuideStrokeWidth,
        );
    }, [pathSvg, showPathGuides, pathGuideStroke, pathGuideStrokeWidth]);

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(" ").map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    return (
        <div className="panel-viewer path-populate-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            style={{ touchAction: "none" }}
                            {...panHandlers}
                        >
                            {pathSvg && showPathGuides && guideMarkup && (
                                <g
                                    className="paths-guide"
                                    dangerouslySetInnerHTML={{
                                        __html: guideMarkup,
                                    }}
                                />
                            )}

                            {packRegionRect && (
                                <rect
                                    x={packRegionRect.x}
                                    y={packRegionRect.y}
                                    width={packRegionRect.width}
                                    height={packRegionRect.height}
                                    fill="none"
                                    stroke="#ff5577"
                                    strokeWidth={1}
                                    strokeDasharray="6 4"
                                    vectorEffect="non-scaling-stroke"
                                    pointerEvents="none"
                                />
                            )}

                            {objectInnerSvgs.length > 0 &&
                                placements.map((p, idx) => {
                                    const objectSvg =
                                        objectInnerSvgs[p.objectIndex];
                                    if (!objectSvg) return null;
                                    const placementScale =
                                        scaleFactor * (p.cellSpan ?? 1);
                                    const anchorCoords =
                                        getAnchorCoordinates(anchor);
                                    let offsetX = 0;
                                    let offsetY = 0;
                                    if (objectSvg.dimensions) {
                                        offsetX =
                                            -anchorCoords.x *
                                            objectSvg.dimensions.width *
                                            placementScale;
                                        offsetY =
                                            -anchorCoords.y *
                                            objectSvg.dimensions.height *
                                            placementScale;
                                    }
                                    let svgContent = objectSvg.content;
                                    const ri = p.regionIndex;
                                    if (gradientEnabled) {
                                        if (
                                            gradientMode ===
                                            "random-per-instance"
                                        ) {
                                            const fillColor =
                                                randomHexColorForPlacement(
                                                    ri,
                                                    idx,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            gradientMode ===
                                                "random-palette-per-instance" &&
                                            gradientColors.length >= 1
                                        ) {
                                            const pi =
                                                paletteColorIndexForPlacement(
                                                    ri,
                                                    idx,
                                                    gradientColors.length,
                                                );
                                            const fillColor =
                                                gradientColors[pi] ??
                                                gradientColors[0];
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            gradientMode ===
                                                "random-per-path" &&
                                            gradientColors.length >= 1
                                        ) {
                                            const fillColor =
                                                paletteColorForPathIndex(
                                                    gradientColors,
                                                    ri,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    fillColor,
                                                );
                                        } else if (
                                            (gradientMode === "per-path" ||
                                                gradientMode === "global") &&
                                            gradientColors.length >= 2
                                        ) {
                                            const position =
                                                gradientMode === "per-path"
                                                    ? maxRi > 0
                                                        ? ri / maxRi
                                                        : 0
                                                    : nPl > 1
                                                      ? idx / (nPl - 1)
                                                      : 0;
                                            const gradientColor =
                                                getGradientColor(
                                                    gradientColors,
                                                    position,
                                                    maxGradientSteps,
                                                );
                                            svgContent =
                                                applyFillToSvgContent(
                                                    objectSvg.content,
                                                    gradientColor,
                                                );
                                        }
                                    }

                                    const angleDeg =
                                        (p.angle * 180) / Math.PI;

                                    const placedTransform = `translate(${p.x + offsetX}, ${p.y + offsetY}) rotate(${angleDeg}, ${-offsetX}, ${-offsetY})${
                                        objectSvg.dimensions
                                            ? ""
                                            : ` scale(${placementScale})`
                                    }`;

                                    return (
                                        <g key={idx} transform={placedTransform}>
                                            {objectSvg.dimensions ? (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox={objectSvg.viewBox}
                                                    width={
                                                        objectSvg.dimensions
                                                            .width *
                                                        placementScale
                                                    }
                                                    height={
                                                        objectSvg.dimensions
                                                            .height *
                                                        placementScale
                                                    }
                                                    preserveAspectRatio="xMidYMid meet"
                                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: object fragment from uploaded SVG
                                                    dangerouslySetInnerHTML={{
                                                        __html: svgContent,
                                                    }}
                                                />
                                            ) : (
                                                <g
                                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: object fragment from uploaded SVG
                                                    dangerouslySetInnerHTML={{
                                                        __html: svgContent,
                                                    }}
                                                />
                                            )}
                                        </g>
                                    );
                                })}
                        </svg>
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {renderFooterMeta({
                            pathSvg,
                            objectInnerSvgs,
                            placementComputePending,
                            placementComputeProgress,
                            regionCount,
                            placements,
                            hasGeneratedPlacementsForCurrentParams,
                            estimatedPlacementCount,
                            supportsFillHitTest,
                        })}
                    </p>
                    <div className="path-tool-actions">
                        {placementComputePending && (
                            <button
                                type="button"
                                className="generic-button"
                                onClick={cancelPlacementCompute}
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="button"
                            className="generic-button generic-button--primary"
                            onClick={generatePlacements}
                            disabled={
                                !pathSvg ||
                                objectInnerSvgs.length === 0 ||
                                placementComputePending
                            }
                        >
                            Generate
                        </button>
                        <button
                            type="button"
                            className="generic-button"
                            onClick={handleDownload}
                            disabled={!canExport}
                        >
                            Download SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
